import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReasonField } from "@/components/ReasonField";

interface ForceEndModalProps {
  open: boolean;
  userId: string;
  onForceEnd: (params: { reason: string }) => Promise<void>;
  onClose: () => void;
}

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && err.message.toLowerCase().includes("fetch");
}

export function ForceEndModal({
  open,
  userId,
  onForceEnd,
  onClose,
}: ForceEndModalProps): React.JSX.Element {
  const [reason, setReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setReason("");
      setSubmitting(false);
      setErrorMessage("");
    }
  }, [open]);

  const canSubmit = reason.trim().length > 0 && !submitting;

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setErrorMessage("");
    try {
      await onForceEnd({ reason });
      onClose();
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setErrorMessage("Нет соединения, повторите");
      } else {
        const bffErr = err as { message?: string } | null;
        setErrorMessage(bffErr?.message ?? "Ошибка сервера. Попробуйте ещё раз.");
      }
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !submitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Принудительно завершить подписку</DialogTitle>
        </DialogHeader>

        <div className="py-1 flex flex-col gap-3">
          <ReasonField
            id={`force-end-reason-${userId}`}
            label="Причина"
            value={reason}
            onChange={setReason}
            required
            placeholder="Укажите причину завершения подписки..."
          />

          <div
            role="alert"
            className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.06)] border border-[rgba(196,58,42,0.18)] rounded-[var(--radius-sm)] text-[12px] text-[var(--fg)]"
          >
            <span className="text-[var(--danger)] flex-shrink-0">⚠</span>
            <span>Действие необратимо. Текущий период подписки будет завершён немедленно.</span>
          </div>

          {errorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.08)] border border-[rgba(196,58,42,0.2)] rounded-[var(--radius-sm)] text-[12px] text-[var(--danger)]"
            >
              <span>✕</span>
              <span>{errorMessage}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 mt-2 border-t border-[var(--border)]">
          <span className="text-[11px] text-[var(--muted)] flex-1 leading-relaxed pr-3">
            Зафиксируется в Audit Log
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Отмена
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => { void handleSubmit(); }}
              disabled={!canSubmit}
            >
              {submitting ? "Загрузка..." : "⚠ Завершить подписку"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
